import { getFieldPropertiesByTarget } from '@wix/form-viewer/controller';
import { IHttpClient } from '@wix/http-client';
import {
  cashierContactToUpdateAddressRequest,
  cashierContactToUpdateContactRequest,
} from '../utils/addresses.utils';
import type { EcomPlatformViewerScriptContext } from '../../../../viewer-script/types';
import { FORMS_TEMPLATE_IDS, FQDN, FormFieldTarget } from '../utils/consts';
import type { ErrorHandlerPublicAPI } from '@wix/error-handler';
import { CartModel } from '../models/Cart.model';
import { SummaryModel } from '../models/Summary.model';
import { CashierContact } from '../checkoutTypes';
import { createDeliveryOptionsServiceGetter } from '../deliveryOptionsGetter';
import type { DeliveryOptionsService } from '@wix/delivery-section-checkout/services';
import { MappedDeliveryOptions } from '@wix/delivery-section-cart/dist/types/types';
import { getShippingMethods } from '../utils/getShippingMethods';
import { ExpressShippingMethod } from '../types';
import type { CartService } from '../../../../services/cart-service/CartService';
import { cartV2 } from '@wix/ecom';

export class CheckoutService {
  private readonly httpClient: IHttpClient;
  private readonly errorHandler: ErrorHandlerPublicAPI;
  private readonly context: EcomPlatformViewerScriptContext;
  private readonly cartService: CartService;
  private readonly deliveryOptionsServiceGetter: () => Promise<DeliveryOptionsService>;

  constructor({
    httpClient,
    errorHandler,
    context,
    cartService,
    locale,
  }: {
    httpClient: IHttpClient;
    errorHandler: ErrorHandlerPublicAPI;
    context: EcomPlatformViewerScriptContext;
    cartService: CartService;
    locale: string;
  }) {
    this.httpClient = httpClient;
    this.errorHandler = errorHandler;
    this.context = context;
    this.cartService = cartService;

    this.deliveryOptionsServiceGetter = createDeliveryOptionsServiceGetter(
      locale,
      httpClient,
      context,
      errorHandler,
    );
  }

  public async getCheckoutByCheckoutId(
    checkoutId: string,
  ): Promise<{ cart: CartModel; summary: SummaryModel }> {
    const { cart, summary } = await this.cartService.calculateCart(checkoutId);
    return {
      cart: CartModel.fromV2(cart!, this.getModelContext(cart!)),
      summary: SummaryModel.fromV2(summary!, this.getModelContext(cart!)),
    };
  }

  public async doesFormHaveRequiredDataExtendedFields(): Promise<boolean> {
    const fieldProps = await getFieldPropertiesByTarget({
      options: { fqdn: FQDN },
      httpClient: this.httpClient,
      formId: FORMS_TEMPLATE_IDS.CUSTOMER_DETAILS_WO_ADDITIONAL_INFO,
      errorHandler: this.errorHandler,
    });

    const fieldValues = fieldProps ? Object.values(fieldProps) : [];

    return fieldValues.filter((fieldValue) => fieldValue?.required).length > 0;
  }

  public async doesFormHaveRequiredAdditionalInfoField(): Promise<boolean> {
    const fieldProps = await getFieldPropertiesByTarget({
      httpClient: this.httpClient,
      formId: FORMS_TEMPLATE_IDS.CUSTOMER_DETAILS_WO_ADDITIONAL_INFO,
      errorHandler: this.errorHandler,
    });

    return Boolean(
      fieldProps && fieldProps[FormFieldTarget.additional_info_1]?.required,
    );
  }

  public async placeOrder({
    id,
    paymentToken,
    redirectUrls,
    priceVerificationToken,
  }: {
    id: string;
    paymentToken: string;
    redirectUrls: {
      errorUrl: string;
      cancelUrl: string;
      successUrl: string;
      pendingUrl: string;
    };
    priceVerificationToken?: string;
  }) {
    const data = await this.cartService.placeOrder(id, {
      paymentToken,
      redirectUrls,
      priceVerificationToken,
    });
    const orderId = data.orderId;
    const paymentResponseToken = data.paymentResponseToken;

    return { orderId, paymentResponseToken };
  }

  public async updateDeliveryAddress(
    checkoutId: string,
    address: CashierContact,
  ): Promise<{
    cart: CartModel;
    summary: SummaryModel;
    shippingMethods?: ExpressShippingMethod[];
    deliveryOptionsErrors?: {
      hasShippingZipCodeError?: boolean;
      hasShippingSubdivisionError?: boolean;
    };
  }> {
    const cart = await this.cartService.updateCart(checkoutId, {
      deliveryInfo: {
        address: cashierContactToUpdateAddressRequest(address),
      },
    });

    const [{ cart: updatedCart, summary }, deliveryOptions] = await Promise.all(
      [
        this.cartService.calculateCart(cart._id!),
        this.fetchDeliveryOptions(cart),
      ],
    );

    const cartModel = CartModel.fromV2(
      updatedCart!,
      this.getModelContext(updatedCart!),
    );
    const summaryModel = SummaryModel.fromV2(
      summary!,
      this.getModelContext(updatedCart!),
    );

    return {
      cart: cartModel,
      summary: summaryModel,
      shippingMethods: getShippingMethods(
        deliveryOptions,
        cartModel.deliveryMethodCode,
      ),
      deliveryOptionsErrors: {
        hasShippingZipCodeError:
          deliveryOptions.errors?.hasShippingZipCodeError ??
          /* istanbul ignore next */ false,
        hasShippingSubdivisionError:
          deliveryOptions.errors?.hasShippingSubdivisionError ??
          /* istanbul ignore next */ false,
      },
    };
  }

  public async updateDeliveryAndBillingInfo(
    checkoutId: string,
    billingContact: CashierContact | undefined,
    shippingContact: CashierContact | undefined,
    { shouldUpdateShippingAddress }: { shouldUpdateShippingAddress: boolean },
  ): Promise<CartModel> {
    const cart = await this.cartService.updateCart(checkoutId, {
      customerInfo: {
        email: billingContact?.email,
        ...cashierContactToUpdateContactRequest(shippingContact),
      },
      ...(shouldUpdateShippingAddress && {
        deliveryInfo: {
          address: cashierContactToUpdateAddressRequest(shippingContact),
        },
      }),
      paymentInfo: {
        billingAddress: cashierContactToUpdateAddressRequest(billingContact),
        billingContact: cashierContactToUpdateContactRequest(billingContact),
      },
    });

    return CartModel.fromV2(cart, this.getModelContext(cart));
  }

  public async setSelectedPaymentMethod(
    checkoutId: string,
    sitePaymentMethodTypeId: string,
  ): Promise<void> {
    await this.cartService.updateCart(checkoutId, {
      paymentInfo: { sitePaymentMethodTypeId },
    });
  }

  public async clearSelectedPaymentMethod(checkoutId: string): Promise<void> {
    await this.cartService.updateCart(checkoutId, {
      paymentInfo: { sitePaymentMethodTypeId: null },
    });
  }

  public async setDeliveryMethod(
    checkoutId: string,
    code: string,
  ): Promise<{ cart: CartModel; summary: SummaryModel }> {
    const cart = await this.cartService.setDeliveryMethod(checkoutId, {
      code,
    });
    const { summary } = await this.cartService.calculateCart(checkoutId);
    return {
      cart: CartModel.fromV2(cart, this.getModelContext(cart)),
      summary: SummaryModel.fromV2(summary!, this.getModelContext(cart)),
    };
  }

  private readonly fetchDeliveryOptions = async (
    cart: cartV2.Cart,
  ): Promise<MappedDeliveryOptions> => {
    const service = await this.deliveryOptionsServiceGetter();

    return service.getDeliveryOptionsFromCartV2(
      cart,
      cart.deliveryInfo?.method?.code,
    );
  };

  private getModelContext(cart: cartV2.Cart) {
    return {
      useConvertedPrices:
        cart.customerInfo?.currencyCode !== cart.businessInfo?.currencyCode &&
        cart.paymentInfo?.currencyCode === cart.customerInfo?.currencyCode,
      experiments: this.context.platformServices!.essentials.experiments,
    };
  }
}
